import React, { useEffect, useState } from 'react';
import { Share2, Copy, Link2, X, Upload, Loader } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface ShareLink {
  id: string;
  folder_id: string;
  token: string;
  allow_upload: boolean;
  max_folder_size_bytes: number;
  max_file_size_bytes: number;
  show_upload_box: boolean;
  created_at: string;
}

interface ShareFolderModalProps {
  folderId: string;
  folderName: string;
  onClose: () => void;
}

const BASE_URL = window.location.origin;

export const ShareFolderModal: React.FC<ShareFolderModalProps> = ({ folderId, folderName, onClose }) => {
  const [link, setLink] = useState<ShareLink | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allowUpload, setAllowUpload] = useState(false);
  const [enableLimits, setEnableLimits] = useState(false);
  const [maxFolderGb, setMaxFolderGb] = useState('');
  const [maxFileMb, setMaxFileMb] = useState('');
  const [showUploadBox, setShowUploadBox] = useState(true);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (message: string) => {
    setToast(message);
    setTimeout(() => setToast(null), 2500);
  };

  const generateToken = () => {
    const bytes = new Uint8Array(24);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  };

  const applySettings = (shareLink: ShareLink) => {
    setLink(shareLink);
    setAllowUpload(shareLink.allow_upload);
    setShowUploadBox(shareLink.show_upload_box !== false);
    setEnableLimits(shareLink.max_folder_size_bytes > 0 || shareLink.max_file_size_bytes > 0);
    setMaxFolderGb(shareLink.max_folder_size_bytes > 0 ? String(shareLink.max_folder_size_bytes / (1024 * 1024 * 1024)) : '');
    setMaxFileMb(shareLink.max_file_size_bytes > 0 ? String(shareLink.max_file_size_bytes / (1024 * 1024)) : '');
  };

  const loadOrCreateLink = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('storage_share_links')
        .select('*')
        .eq('folder_id', folderId)
        .order('created_at', { ascending: true })
        .limit(1);

      if (error) throw error;

      if (data && data.length > 0) {
        applySettings(data[0] as ShareLink);
        return;
      }

      const { data: created, error: createError } = await supabase
        .from('storage_share_links')
        .insert({
          folder_id: folderId,
          token: generateToken(),
          allow_upload: false,
          show_upload_box: true,
          max_folder_size_bytes: 0,
          max_file_size_bytes: 0,
        })
        .select('*')
        .single();

      if (createError || !created) throw createError || new Error('Link creation failed');
      applySettings(created as ShareLink);
    } catch (error) {
      console.error('Share link load/create error:', error);
      showToast('Failed to create or load link');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrCreateLink();
  }, [folderId]);

  const saveSettings = async () => {
    if (!link) return;
    setSaving(true);
    try {
      const maxFolderBytes = enableLimits && maxFolderGb
        ? Math.round(parseFloat(maxFolderGb.replace(',', '.')) * 1024 * 1024 * 1024)
        : 0;
      const maxFileBytes = enableLimits && maxFileMb
        ? Math.round(parseFloat(maxFileMb.replace(',', '.')) * 1024 * 1024)
        : 0;

      const { data, error } = await supabase
        .from('storage_share_links')
        .update({
          allow_upload: allowUpload,
          show_upload_box: showUploadBox,
          max_folder_size_bytes: maxFolderBytes,
          max_file_size_bytes: maxFileBytes,
        })
        .eq('id', link.id)
        .select('*')
        .single();

      if (error || !data) throw error || new Error('Update failed');
      applySettings(data as ShareLink);
      showToast('Settings saved');
    } catch (error) {
      console.error('Share link update error:', error);
      showToast('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const copyLink = async () => {
    if (!link) return;
    const url = `${BASE_URL}/share/${link.token}`;
    try {
      await navigator.clipboard.writeText(url);
      showToast('Link copied');
    } catch {
      showToast(url);
    }
  };

  const formatLimit = (bytes: number): string => {
    if (bytes === 0) return 'unlimited';
    if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-dark-800 border border-gray-200 dark:border-gray-700 rounded-xl w-full max-w-lg max-h-[80vh] flex flex-col shadow-xl"
        onClick={event => event.stopPropagation()}
      >
        {toast && (
          <div className="absolute top-4 right-4 z-50 bg-gray-900 dark:bg-dark-700 border border-gray-600 text-white text-sm px-4 py-2 rounded-lg shadow-lg">
            {toast}
          </div>
        )}

        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary-500 flex items-center justify-center">
              <Share2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">Share folder</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[300px]">{folderName}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex justify-center py-10"><Loader className="w-6 h-6 animate-spin text-primary-500" /></div>
          ) : link ? (
            <>
              <div className="bg-gray-50 dark:bg-dark-700/50 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                <div className="flex items-center gap-2 min-w-0">
                  <Link2 className="w-4 h-4 text-primary-500 flex-shrink-0" />
                  <span className="truncate font-mono text-xs text-gray-600 dark:text-gray-300 flex-1">
                    {`${BASE_URL}/share/${link.token}`}
                  </span>
                  <button
                    onClick={copyLink}
                    className="btn !px-2 !py-1 !text-xs flex-shrink-0"
                    title="Copy link"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    Copy
                  </button>
                </div>
              </div>

              <div className="mt-4 bg-gray-50 dark:bg-dark-700/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Link settings</h3>

                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 mb-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allowUpload}
                    onChange={event => setAllowUpload(event.target.checked)}
                    className="w-4 h-4 accent-primary-500"
                  />
                  <Upload className="w-4 h-4 text-primary-500" />
                  Allow upload through this link
                </label>

                {allowUpload && (
                  <div className="ml-6 space-y-3 border-l-2 border-gray-200 dark:border-gray-600 pl-4">
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showUploadBox}
                        onChange={event => setShowUploadBox(event.target.checked)}
                        className="w-4 h-4 accent-primary-500"
                      />
                      Show upload area on the page
                    </label>

                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={enableLimits}
                        onChange={event => setEnableLimits(event.target.checked)}
                        className="w-4 h-4 accent-primary-500"
                      />
                      Enable size limits
                    </label>

                    {enableLimits && (
                      <div className="grid grid-cols-2 gap-3">
                        <label className="block text-xs text-gray-500 dark:text-gray-400">
                          Max folder size (GB)
                          <input
                            type="number"
                            min="0"
                            step="0.1"
                            value={maxFolderGb}
                            onChange={event => setMaxFolderGb(event.target.value)}
                            placeholder="0 = unlimited"
                            className="mt-1 w-full px-3 py-1.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                          />
                        </label>
                        <label className="block text-xs text-gray-500 dark:text-gray-400">
                          Max file size (MB)
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={maxFileMb}
                            onChange={event => setMaxFileMb(event.target.value)}
                            placeholder="0 = unlimited"
                            className="mt-1 w-full px-3 py-1.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                          />
                        </label>
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-4 flex items-center justify-between gap-2">
                  <div className="flex flex-wrap gap-1.5 text-[11px]">
                    <span className={`px-2 py-0.5 rounded-full ${allowUpload ? 'bg-green-500/15 text-green-600 dark:text-green-400' : 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-300'}`}>
                      {allowUpload ? 'Upload enabled' : 'View + download only'}
                    </span>
                    {allowUpload && (
                      <>
                        <span className="px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-300">
                          Folder: {formatLimit(enableLimits && maxFolderGb ? Number(maxFolderGb.replace(',', '.')) * 1024 * 1024 * 1024 : 0)}
                        </span>
                        <span className="px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-300">
                          File: {formatLimit(enableLimits && maxFileMb ? Number(maxFileMb.replace(',', '.')) * 1024 * 1024 : 0)}
                        </span>
                      </>
                    )}
                  </div>
                  <button
                    onClick={saveSettings}
                    disabled={saving}
                    className="btn disabled:opacity-50 disabled:cursor-not-allowed !px-3 !py-2 flex-shrink-0"
                  >
                    {saving ? <Loader className="w-4 h-4 animate-spin" /> : null}
                    Save settings
                  </button>
                </div>
              </div>
            </>
          ) : (
            <p className="text-sm text-red-500">The share link could not be created.</p>
          )}
        </div>
      </div>
    </div>
  );
};
